import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iconBookStep2Courses, iconPlusWhiteStep2Courses } from '../../assets';
import type { MultilingualText } from './CreateCourseStepOne';
import { FileLessonModal } from './FileLessonModal';
import { ModuleEditor } from './ModuleEditor';
import {
  QuizLessonModal,
  type FetchQuestionsForQuizFn,
  type QuizLessonConfig,
} from './QuizLessonModal';
import { VideoLessonModal } from './VideoLessonModal';
import { useTheme } from '../../theme';

export type LessonType = 'video' | 'file' | 'quiz';

export interface CourseLesson {
  id: string;
  type: LessonType;
  xp: number;
  /** Nome da lição em EN/ES (ex.: para exibição no Step 2). */
  name?: MultilingualText;
  /** URL do vídeo Vimeo quando type === 'video' */
  videoUrl?: string;
  /** Arquivos novos anexados quando type === 'file'. */
  files?: File[];
  /** Arquivos existentes na API (id/nome); usados para exibição e remoção na edição. */
  courseModuleLessonFiles?: { id: string; name: string }[];
  /** Snapshot inicial dos arquivos existentes; usado para detectar quais foram removidos na edição. */
  originalCourseModuleLessonFiles?: { id: string; name: string }[];
  /** Id da config de quiz na API (course-module-lesson-quiz-config); usado no Save Draft para PUT questions quando quiz manual. */
  courseModuleLessonQuizConfigId?: string;
  /** Configuração quando type === 'quiz' */
  quizConfig?: QuizLessonConfig;
}

export interface CourseModule {
  id: string;
  name: MultilingualText;
  lessons: CourseLesson[];
}

/** Erros por campo de um módulo (chaves i18n em courses.create.*). */
export interface StepTwoModuleErrors {
  moduleName?: string;
  lessons?: string;
}

/** Resultado da validação do Step 2 (curriculum). */
export interface StepTwoValidationResult {
  valid: boolean;
  /** Erro de seção quando não há módulos (ex.: abaixo do botão Add Module) */
  sectionErrorKey?: string;
  /** Erros por módulo (id → erros abaixo do Module Name e da área de lições) */
  moduleErrors?: Record<string, StepTwoModuleErrors>;
}

/**
 * Valida o Step 2: pelo menos 1 módulo com Module Name preenchido e pelo menos 1 lição (vídeo, arquivo ou quiz).
 * Retorna erros por módulo para exibir abaixo dos campos, no padrão do Step One.
 */
export function validateStepTwo(modules: CourseModule[]): StepTwoValidationResult {
  if (!modules || modules.length === 0) {
    return {
      valid: false,
      sectionErrorKey: 'validation_curriculum_no_modules',
      moduleErrors: {},
    };
  }
  const moduleErrors: Record<string, StepTwoModuleErrors> = {};
  let hasValidModule = false;
  for (const m of modules) {
    const hasName =
      (m.name?.en?.trim() ?? '') !== '' || (m.name?.es?.trim() ?? '') !== '';
    const hasLesson = (m.lessons?.length ?? 0) > 0;
    if (hasName && hasLesson) hasValidModule = true;
    if (!hasName || !hasLesson) {
      moduleErrors[m.id] = {};
      if (!hasName) moduleErrors[m.id].moduleName = 'validation_module_name_required';
      if (!hasLesson) moduleErrors[m.id].lessons = 'validation_module_lessons_required';
    }
  }
  return {
    valid: hasValidModule,
    sectionErrorKey: hasValidModule ? undefined : undefined,
    moduleErrors: Object.keys(moduleErrors).length > 0 ? moduleErrors : undefined,
  };
}

type LangKey = 'en' | 'es';

function generateId(): string {
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const defaultModule = (): CourseModule => ({
  id: generateId(),
  name: { en: '', es: '' },
  lessons: [],
});

export interface CreateCourseStepTwoProps {
  initialModules?: CourseModule[];
  onChange?: (modules: CourseModule[]) => void;
  /** Erro de seção (ex.: nenhum módulo) – exibido abaixo do header, padrão Step One */
  sectionValidationErrorKey?: string;
  /** Erros por módulo (id → moduleName/lessons) – repassados ao ModuleEditor, exibidos abaixo dos campos */
  moduleValidationErrors?: Record<string, StepTwoModuleErrors>;
  /** Carrega lição por id (GET course-module-lesson) para preencher o modal de edição de quiz */
  loadLessonForEdit?: (lessonId: string) => Promise<CourseLesson | null>;
  /** Listagem paginada de perguntas para seleção manual no quiz (v1/Question/pagination) */
  fetchQuestionsForQuiz?: FetchQuestionsForQuizFn;
}

export function CreateCourseStepTwo({
  initialModules = [],
  onChange,
  sectionValidationErrorKey,
  moduleValidationErrors,
  loadLessonForEdit,
  fetchQuestionsForQuiz,
}: CreateCourseStepTwoProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [modules, setModules] = useState<CourseModule[]>(
    initialModules.length > 0 ? initialModules : [defaultModule()]
  );
  const [moduleNameLang, setModuleNameLang] = useState<Record<string, LangKey>>(
    () => ({} as Record<string, LangKey>)
  );
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoModalModuleId, setVideoModalModuleId] = useState<string | null>(
    null
  );
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileModalModuleId, setFileModalModuleId] = useState<string | null>(
    null
  );
  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const [quizModalModuleId, setQuizModalModuleId] = useState<string | null>(
    null
  );
  /** Lição sendo editada (abre o modal video/file/quiz correspondente) */
  const [editingLesson, setEditingLesson] = useState<{
    moduleId: string;
    lesson: CourseLesson;
  } | null>(null);
  /** Lição carregada via GET course-module-lesson para edição de quiz (quantidades, booleanos, questões) */
  const [loadedQuizLesson, setLoadedQuizLesson] = useState<{
    lessonId: string;
    lesson: CourseLesson;
  } | null>(null);
  /** ID do módulo sendo arrastado (reorder de módulos). */
  const [draggedModuleId, setDraggedModuleId] = useState<string | null>(null);
  /** ID do módulo sobre o qual o cursor está (alvo do drop – fica mais apagado). */
  const [dragOverModuleId, setDragOverModuleId] = useState<string | null>(null);

  const getModuleLang = (moduleId: string): LangKey =>
    moduleNameLang[moduleId] ?? 'en';

  const setModuleLang = (moduleId: string, lang: LangKey) => {
    setModuleNameLang(prev => ({ ...prev, [moduleId]: lang }));
  };

  useEffect(() => {
    if (
      !quizModalOpen ||
      editingLesson?.lesson.type !== 'quiz' ||
      !editingLesson?.lesson.id ||
      editingLesson.lesson.id.startsWith('id-') ||
      !loadLessonForEdit
    ) {
      return;
    }
    let cancelled = false;
    loadLessonForEdit(editingLesson.lesson.id)
      .then(lesson => {
        if (!cancelled && lesson)
          setLoadedQuizLesson({ lessonId: editingLesson.lesson.id, lesson });
        else if (!cancelled) setLoadedQuizLesson(null);
      })
      .catch(() => {
        if (!cancelled) setLoadedQuizLesson(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    quizModalOpen,
    editingLesson?.lesson.type,
    editingLesson?.lesson.id,
    loadLessonForEdit,
  ]);

  const updateModules = useCallback(
    (next: CourseModule[]) => {
      setModules(next);
      onChange?.(next);
    },
    [onChange]
  );

  const handleAddModule = () => {
    updateModules([...modules, defaultModule()]);
  };

  const handleRemoveModule = (moduleId: string) => {
    updateModules(modules.filter(m => m.id !== moduleId));
  };

  const handleModuleDragStart = useCallback(
    (e: React.DragEvent, moduleId: string) => {
      e.dataTransfer.setData('application/x-module-id', moduleId);
      e.dataTransfer.effectAllowed = 'move';
      setDraggedModuleId(moduleId);
      setDragOverModuleId(null);
    },
    []
  );

  const handleModuleDragEnd = useCallback(() => {
    setDraggedModuleId(null);
    setDragOverModuleId(null);
  }, []);

  const handleModuleDragLeave = useCallback(() => {
    setDragOverModuleId(null);
  }, []);

  const handleModuleDragOver = useCallback(
    (e: React.DragEvent, targetModuleId: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverModuleId(targetModuleId);
    },
    []
  );

  const handleModuleDrop = useCallback(
    (e: React.DragEvent, targetModuleId: string) => {
      e.preventDefault();
      setDragOverModuleId(null);
      const sourceId = e.dataTransfer.getData('application/x-module-id');
      if (!sourceId || sourceId === targetModuleId) return;
      const fromIndex = modules.findIndex(m => m.id === sourceId);
      const toIndex = modules.findIndex(m => m.id === targetModuleId);
      if (fromIndex === -1 || toIndex === -1) return;
      const next = [...modules];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      updateModules(next);
      setDraggedModuleId(null);
    },
    [modules, updateModules]
  );

  const handleModuleNameChange = (moduleId: string, lang: LangKey, value: string) => {
    updateModules(
      modules.map(m =>
        m.id === moduleId ? { ...m, name: { ...m.name, [lang]: value } } : m
      )
    );
  };

  const handleAddLesson = (
    moduleId: string,
    type: LessonType,
    videoUrl?: string,
    files?: File[],
    quizConfig?: QuizLessonConfig,
    lessonName?: MultilingualText
  ) => {
    const newLesson: CourseLesson = {
      id: generateId(),
      type,
      xp: 10,
      ...(type === 'video' && lessonName && { name: lessonName }),
      ...(type === 'video' && videoUrl != null && { videoUrl }),
      ...(type === 'file' && files != null && { files }),
      ...(type === 'quiz' && quizConfig != null && { quizConfig }),
    };
    updateModules(
      modules.map(m =>
        m.id === moduleId
          ? { ...m, lessons: [...m.lessons, newLesson] }
          : m
      )
    );
  };

  const handleRemoveLesson = (moduleId: string, lessonId: string) => {
    updateModules(
      modules.map(m =>
        m.id === moduleId
          ? { ...m, lessons: m.lessons.filter(l => l.id !== lessonId) }
          : m
      )
    );
  };

  const handleLessonXpChange = (moduleId: string, lessonId: string, xp: number) => {
    updateModules(
      modules.map(m =>
        m.id === moduleId
          ? {
              ...m,
              lessons: m.lessons.map(l =>
                l.id === lessonId ? { ...l, xp } : l
              ),
            }
          : m
      )
    );
  };

  const handleUpdateLesson = (
    moduleId: string,
    lessonId: string,
    type: LessonType,
    videoUrl?: string,
    files?: File[],
    quizConfig?: QuizLessonConfig,
    lessonName?: MultilingualText,
    existingFiles?: { id: string; name: string }[]
  ) => {
    updateModules(
      modules.map(m =>
        m.id === moduleId
          ? {
              ...m,
              lessons: m.lessons.map(l => {
                if (l.id !== lessonId) return l;
                return {
                  ...l,
                  type,
                  ...(type === 'video' && lessonName && { name: lessonName }),
                  ...(type === 'video' && videoUrl != null && { videoUrl }),
                  ...(type === 'file' && files != null && { files }),
                  ...(type === 'file' && existingFiles != null && { courseModuleLessonFiles: existingFiles }),
                  ...(type === 'quiz' && quizConfig != null && { quizConfig }),
                };
              }),
            }
          : m
      )
    );
  };

  const handleLessonMove = useCallback(
    (moduleId: string, fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      updateModules(
        modules.map(m => {
          if (m.id !== moduleId || fromIndex < 0 || toIndex < 0) return m;
          const list = [...m.lessons];
          if (fromIndex >= list.length || toIndex >= list.length) return m;
          const [removed] = list.splice(fromIndex, 1);
          list.splice(toIndex, 0, removed);
          return { ...m, lessons: list };
        })
      );
    },
    [modules, updateModules]
  );

  const openEditLessonModal = (moduleId: string, lesson: CourseLesson) => {
    setEditingLesson({ moduleId, lesson });
    if (lesson.type === 'video') {
      setVideoModalModuleId(moduleId);
      setVideoModalOpen(true);
    } else if (lesson.type === 'file') {
      setFileModalModuleId(moduleId);
      setFileModalOpen(true);
    } else if (lesson.type === 'quiz') {
      setQuizModalModuleId(moduleId);
      setQuizModalOpen(true);
    }
  };

  const sc = theme.palette.createCourse;

  const maskedIconPrimaryGreen = (
    src: string,
    sizePx: number
  ): React.CSSProperties => ({
    width: sizePx,
    height: sizePx,
    backgroundColor: theme.palette.text.primary,
    WebkitMaskImage: `url(${src})`,
    WebkitMaskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskImage: `url(${src})`,
    maskSize: 'contain',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    display: 'block',
  });

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.md,
    padding: theme.spacing.xl,
    marginBottom: theme.spacing.xl,
    boxShadow: 'none',
  };

  const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.xl,
    flexWrap: 'wrap',
  };

  const headerLeftStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  };

  const iconBoxStyle: React.CSSProperties = {
    width: sc.iconBoxSize,
    height: sc.iconBoxSize,
    borderRadius: theme.shape.borderRadius.sm2,
    backgroundColor: theme.palette.createCourse.curriculum.iconBoxBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.xl,
    lineHeight: 1.25,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const sectionSubtitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing.xs,
    marginBottom: 0,
  };

  const addModuleButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    lineHeight: 1.25,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    borderRadius: theme.shape.borderRadius.sm2,
    cursor: 'pointer',
    flexShrink: 0,
  };

  const moduleListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.lg,
  };

  const errorStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.error.main,
    marginTop: theme.spacing.xs,
    marginBottom: 0,
  };

  return (
    <div style={cardStyle}>
      <header style={headerRowStyle}>
        <div style={headerLeftStyle}>
          <div style={iconBoxStyle}>
            <div
              style={maskedIconPrimaryGreen(
                iconBookStep2Courses,
                sc.iconSize
              )}
              aria-hidden
            />
          </div>
          <div>
            <h2 style={sectionTitleStyle}>
              {t('courses.create.step_curriculum')}
            </h2>
            <p style={sectionSubtitleStyle}>
              {t('courses.create.curriculum_subtitle')}
            </p>
          </div>
        </div>
        <button
          type="button"
          style={addModuleButtonStyle}
          onClick={handleAddModule}
          aria-label={t('courses.create.add_module_aria')}
        >
          <img
            src={iconPlusWhiteStep2Courses}
            alt=""
            width={sc.addModuleIconSize}
            height={sc.addModuleIconSize}
            style={{ objectFit: 'contain', flexShrink: 0 }}
            aria-hidden
          />
          {t('courses.create.add_module')}
        </button>
      </header>
      {sectionValidationErrorKey && (
        <p style={{ ...errorStyle, marginBottom: theme.spacing.lg }} role="alert">
          {t(`courses.create.${sectionValidationErrorKey}`)}
        </p>
      )}

      <div style={moduleListStyle}>
        {modules.map(module => (
          <ModuleEditor
            key={module.id}
            theme={theme}
            moduleId={module.id}
            defaultExpanded={false}
            moduleName={module.name}
            currentLang={getModuleLang(module.id)}
            onLangChange={lang => setModuleLang(module.id, lang)}
            onModuleNameChange={(lang, value) =>
              handleModuleNameChange(module.id, lang, value)
            }
            lessons={module.lessons}
            validationErrors={moduleValidationErrors?.[module.id]}
            isModuleDragging={draggedModuleId === module.id}
            isModuleDropTarget={dragOverModuleId === module.id}
            onModuleDragStart={handleModuleDragStart}
            onModuleDragOver={handleModuleDragOver}
            onModuleDragLeave={handleModuleDragLeave}
            onModuleDrop={handleModuleDrop}
            onModuleDragEnd={handleModuleDragEnd}
            onLessonMove={(fromIndex, toIndex) =>
              handleLessonMove(module.id, fromIndex, toIndex)
            }
            labels={{
              moduleName: t('courses.create.module_name'),
              moduleNamePlaceholder: t('courses.create.module_name_placeholder'),
              multilingualHint: t('courses.create.multilingual_hint'),
              lessonsCount: count =>
                t('courses.create.lessons_count', { count }),
              addLesson: t('courses.create.add_lesson'),
              addLessonAria: t('courses.create.add_lesson_aria'),
              deleteModuleAria: t('courses.create.delete_module_aria'),
              deleteLessonAria: t('courses.create.delete_lesson_aria'),
              editLessonAria: t('courses.create.edit_lesson_aria'),
              lessonXpAria: t('courses.create.lesson_xp_aria'),
              dragHandle: t('courses.create.drag_handle'),
              expandModuleAria: t('courses.create.expand_module_aria'),
              collapseModuleAria: t('courses.create.collapse_module_aria'),
              moduleNameLanguageAria: t('courses.create.module_name_language_aria'),
              lessonTypeVideo: t('courses.create.lesson_type_video'),
              lessonTypeFile: t('courses.create.lesson_type_file'),
              lessonTypeQuiz: t('courses.create.lesson_type_quiz'),
              lesson: t('courses.create.lesson'),
            }}
            onAddLesson={type => handleAddLesson(module.id, type)}
            onAddVideoLessonRequest={() => {
              setVideoModalModuleId(module.id);
              setVideoModalOpen(true);
            }}
            onAddFileLessonRequest={() => {
              setFileModalModuleId(module.id);
              setFileModalOpen(true);
            }}
            onAddQuizLessonRequest={() => {
              setQuizModalModuleId(module.id);
              setQuizModalOpen(true);
            }}
            onDeleteModule={() => handleRemoveModule(module.id)}
            onDeleteLesson={lessonId => handleRemoveLesson(module.id, lessonId)}
            onEditLesson={lesson => openEditLessonModal(module.id, lesson)}
            onLessonXpChange={(lessonId, xp) =>
              handleLessonXpChange(module.id, lessonId, xp)
            }
          />
        ))}
      </div>

      <VideoLessonModal
        open={videoModalOpen}
        onClose={() => {
          setVideoModalOpen(false);
          setVideoModalModuleId(null);
          setEditingLesson(null);
        }}
        initialVimeoUrl={
          editingLesson?.lesson.type === 'video' && editingLesson.moduleId === videoModalModuleId
            ? (editingLesson.lesson.videoUrl ?? '')
            : ''
        }
        initialName={
          editingLesson?.lesson.type === 'video' && editingLesson.moduleId === videoModalModuleId
            ? {
                en: editingLesson.lesson.name?.en ?? '',
                es: editingLesson.lesson.name?.es ?? '',
              }
            : undefined
        }
        isEditMode={
          Boolean(
            editingLesson?.lesson.type === 'video' &&
              editingLesson.moduleId === videoModalModuleId &&
              editingLesson.lesson.id &&
              !editingLesson.lesson.id.startsWith('id-')
          )
        }
        onConfirm={(vimeoUrl, name) => {
          if (!videoModalModuleId) return;
          const isEditing =
            editingLesson &&
            editingLesson.lesson.type === 'video' &&
            editingLesson.moduleId === videoModalModuleId;
          if (isEditing) {
            handleUpdateLesson(
              editingLesson.moduleId,
              editingLesson.lesson.id,
              'video',
              vimeoUrl,
              undefined,
              undefined,
              name
            );
            setEditingLesson(null);
          } else {
            handleAddLesson(videoModalModuleId, 'video', vimeoUrl, undefined, undefined, name);
          }
          setVideoModalOpen(false);
          setVideoModalModuleId(null);
        }}
      />

      <FileLessonModal
        open={fileModalOpen}
        onClose={() => {
          setFileModalOpen(false);
          setFileModalModuleId(null);
          setEditingLesson(null);
        }}
        initialFiles={
          editingLesson?.lesson.type === 'file' && editingLesson.moduleId === fileModalModuleId
            ? (editingLesson.lesson.files ?? [])
            : []
        }
        initialExistingFiles={
          editingLesson?.lesson.type === 'file' && editingLesson.moduleId === fileModalModuleId
            ? (editingLesson.lesson.courseModuleLessonFiles ?? [])
            : []
        }
        onConfirm={({ files, existingFiles }) => {
          if (fileModalModuleId) {
            if (
              editingLesson &&
              editingLesson.lesson.type === 'file' &&
              editingLesson.moduleId === fileModalModuleId
            ) {
              handleUpdateLesson(
                editingLesson.moduleId,
                editingLesson.lesson.id,
                'file',
                undefined,
                files,
                undefined,
                undefined,
                existingFiles
              );
              setEditingLesson(null);
            } else {
              handleAddLesson(fileModalModuleId, 'file', undefined, files);
            }
          }
          setFileModalOpen(false);
          setFileModalModuleId(null);
        }}
      />

      <QuizLessonModal
        open={quizModalOpen}
        onClose={() => {
          setQuizModalOpen(false);
          setQuizModalModuleId(null);
          setEditingLesson(null);
          setLoadedQuizLesson(null);
        }}
        initialConfig={
          editingLesson?.lesson.type === 'quiz' && editingLesson.moduleId === quizModalModuleId
            ? (loadedQuizLesson?.lessonId === editingLesson?.lesson.id
                ? loadedQuizLesson.lesson.quizConfig
                : editingLesson.lesson.quizConfig) ?? null
            : null
        }
        availableQuestions={[]}
        fetchQuestions={fetchQuestionsForQuiz}
        onConfirm={quizConfig => {
          if (quizModalModuleId) {
            if (
              editingLesson &&
              editingLesson.lesson.type === 'quiz' &&
              editingLesson.moduleId === quizModalModuleId
            ) {
              handleUpdateLesson(
                editingLesson.moduleId,
                editingLesson.lesson.id,
                'quiz',
                undefined,
                undefined,
                quizConfig
              );
              setEditingLesson(null);
            } else {
              handleAddLesson(
                quizModalModuleId,
                'quiz',
                undefined,
                undefined,
                quizConfig
              );
            }
          }
          setQuizModalOpen(false);
          setQuizModalModuleId(null);
        }}
      />
    </div>
  );
}
